/**
 * Document Requirement Configurator
 * Admin interface for creating and managing document requirements
 * This demonstrates how templates are associated with roles, programs, and entities
 */

import React, { useState, useEffect } from 'react';
import documentRequirementsService from '../../services/documentRequirementsService';
import clubDocumentApi from '../../services/clubDocumentApi';
import apiService from '../../services/api';
import { getNavigationIcon } from '../navigation/NavigationIcons';
import { Section, Grid, Col, FormInput, FormSelect, FormTextArea, FormActionGroup } from '../../components';
import { useToast } from '../../contexts/ToastContext';

const DocumentRequirementConfigurator = ({ club_id, requirement, onSave, onCancel }) => {
  const { showSuccess, showError } = useToast();
  const [templates, setTemplates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({
    template_id: '',
    title: '',
    description: '',
    assignment_type: 'club_wide',
    assignment_target_id: null, // Singular: program_id, event_id, etc.
    target_roles: [], // Will be populated with role IDs from /api/roles
    user_targeting: 'all_users',
    is_mandatory: true,
    due_date_offset_days: 7,
    conditions: [],
    send_email_notifications: true,
    send_sms_notifications: false,
    active: true
  });
  const [loading, setLoading] = useState(false);
  const [showConditions, setShowConditions] = useState(false);

  const isEditMode = !!requirement;

  useEffect(() => {
    loadTemplates();
    loadPrograms();
    loadRoles();
  }, [club_id]);

  // Populate form when editing
  useEffect(() => {
    if (requirement) {
      setFormData({
        template_id: requirement.template_id || '',
        title: requirement.title || '',
        description: requirement.description || '',
        assignment_type: requirement.assignment_type || 'club_wide',
        assignment_target_id: requirement.assignment_target_id || null,
        target_roles: requirement.target_roles || [],
        user_targeting: requirement.user_targeting || 'all_users',
        is_mandatory: requirement.is_mandatory !== undefined ? requirement.is_mandatory : true,
        due_date_offset_days: requirement.due_date_offset_days || 7,
        conditions: requirement.conditions || [],
        send_email_notifications: requirement.send_email_notifications !== undefined ? requirement.send_email_notifications : true,
        send_sms_notifications: requirement.send_sms_notifications !== undefined ? requirement.send_sms_notifications : false,
        active: requirement.active !== undefined ? requirement.active : true
      });
      if (requirement.conditions && requirement.conditions.length > 0) {
        setShowConditions(true);
      }
    }
  }, [requirement]);

  const loadTemplates = async () => {
    try {
      // Fetch club templates from API
      const response = await clubDocumentApi.getTemplates(club_id);
      const clubTemplates = response.data?.templates || response.templates || [];

      // Transform to match the expected format
      const formattedTemplates = clubTemplates.map(template => ({
        id: template.id,
        title: template.title,
        category: template.category || 'other'
      }));

      setTemplates(formattedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      // Fallback to empty array on error
      setTemplates([]);
    }
  };

  const loadPrograms = async () => {
    try {
      // Fetch programs from API
      const response = await apiService.get('/api/programs/');
      const programList = response.programs || [];

      // Transform to match the expected format
      const formattedPrograms = programList.map(program => ({
        id: program.id,
        name: program.name,
        category: program.category || 'other'
      }));

      setPrograms(formattedPrograms);
    } catch (error) {
      console.error('Error loading programs:', error);
      // Fallback to empty array on error
      setPrograms([]);
    }
  };

  const loadRoles = async () => {
    try {
      // Fetch roles from API - use club-specific roles endpoint with active_only filter
      const response = await apiService.get('/api/clubs/roles?active_only=true');
      const roleList = response.roles || [];

      // Transform to match the expected format for display
      const formattedRoles = roleList
        .filter(role => role.is_active) // Only show active roles
        .map(role => ({
          id: role.id,
          name: role.name,
          description: role.description,
          is_staff: role.is_staff || false
        }));

      setRoles(formattedRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
      // Fallback to empty array on error
      setRoles([]);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  const handleProgramSelect = (programId) => {
    setFormData(prev => ({
      ...prev,
      assignment_target_id: programId
    }));
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        {
          condition_type: 'age_comparison',
          field: 'player_age',
          operator: '<',
          value: 16,
          description: ''
        }
      ]
    }));
  };

  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const updateCondition = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) =>
        i === index ? { ...condition, [field]: value } : condition
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditMode) {
        // Update existing requirement
        await documentRequirementsService.updateRequirement(requirement.id, formData);
        showSuccess(`Document requirement "${formData.title}" updated successfully`);
      } else {
        // Create new requirement
        await documentRequirementsService.createRequirement(formData);
        showSuccess(`Document requirement "${formData.title}" created successfully`);
      }
      if (onSave) onSave();
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} requirement:`, error);
      showError(`Failed to ${isEditMode ? 'update' : 'create'} document requirement. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{
        marginBottom: '2rem',
        paddingBottom: '1.5rem',
        borderBottom: '1px solid var(--color-border)'
      }}>
        <h3 style={{
          fontSize: '1.5rem',
          fontWeight: 700,
          color: 'var(--color-text)',
          margin: '0 0 0.5rem 0',
          letterSpacing: '-0.025em'
        }}>
          <span style={{ marginRight: '0.5rem' }}>{getNavigationIcon('clipboard')}</span>
          {isEditMode ? 'EDIT' : 'CREATE'} DOCUMENT REQUIREMENT
        </h3>
        <p style={{ color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.6 }}>
          Configure how and when documents are assigned to club members
        </p>
      </div>

      <form onSubmit={handleSubmit}>
          {/* Template Selection */}
          <div className="form-section">
            <div className="form-section-title">
              <span className="section-icon">{getNavigationIcon('document')}</span>
              1. Select Document Template
            </div>
            <div className="checkbox-group">
              {templates.map(template => (
                <div
                  key={template.id}
                  className={`checkbox-item ${formData.template_id === template.id ? 'selected' : ''}`}
                  onClick={() => {
                    handleInputChange('template_id', template.id);
                    if (!formData.title) {
                      handleInputChange('title', `${template.title} Requirement`);
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.template_id === template.id}
                    onChange={() => {}}
                  />
                  <label>
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                      {template.title}
                    </div>
                    <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                      {template.category}
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Basic Info */}
          <div className="form-section">
            <div className="form-section-title">
              <span className="section-icon">{getNavigationIcon('edit')}</span>
              2. Basic Information
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Requirement Title</label>
                <input
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                  placeholder="Enter requirement title"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Due Date (days after assignment)</label>
                <input
                  className="form-input"
                  type="number"
                  value={formData.due_date_offset_days}
                  onChange={(e) => handleInputChange('due_date_offset_days', parseInt(e.target.value))}
                  min="1"
                  placeholder="7"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe what this requirement is for and any special instructions"
                rows={3}
              />
            </div>
          </div>

          {/* Assignment Type */}
          <div className="form-section">
            <div className="form-section-title">
              <span className="section-icon">{getNavigationIcon('target')}</span>
              3. Assignment Targeting
            </div>
            <div className="form-group">
              <label className="form-label">Assignment Type:</label>
              <div className="checkbox-group">
                {[
                  { value: 'club_wide', label: 'Club-Wide', desc: 'All club members', icon: 'building' },
                  { value: 'program_specific', label: 'Program-Specific', desc: 'Specific program participants', icon: 'soccer' },
                  { value: 'event_specific', label: 'Event-Specific', desc: 'Specific event attendees', icon: 'target' },
                  { value: 'role_specific', label: 'Role-Specific', desc: 'Specific user roles only', icon: 'profile' },
                  { value: 'conditional', label: 'Conditional', desc: 'Based on conditions', icon: 'search' }
                ].map(type => (
                  <div
                    key={type.value}
                    className={`checkbox-item ${formData.assignment_type === type.value ? 'selected' : ''}`}
                    onClick={() => handleInputChange('assignment_type', type.value)}
                  >
                    <input
                      type="radio"
                      name="assignment_type"
                      checked={formData.assignment_type === type.value}
                      onChange={() => {}}
                    />
                    <label>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{type.label}</div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>{type.desc}</div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Program Selection for program_specific */}
            {formData.assignment_type === 'program_specific' && (
              <div className="form-group">
                <label className="form-label">Select Program:</label>
                <div className="checkbox-group">
                  {programs.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)', textAlign: 'center', margin: '1rem 0' }}>
                      Loading programs...
                    </p>
                  ) : (
                    programs.map(program => (
                      <div
                        key={program.id}
                        className={`checkbox-item ${formData.assignment_target_id === program.id ? 'selected' : ''}`}
                        onClick={() => handleProgramSelect(program.id)}
                      >
                        <input
                          type="radio"
                          name="program_selection"
                          checked={formData.assignment_target_id === program.id}
                          onChange={() => {}}
                        />
                        <label>
                          <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                            {program.name}
                          </div>
                          <div style={{ fontSize: '0.8rem', opacity: 0.8, textTransform: 'capitalize' }}>
                            {program.category}
                          </div>
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Target Roles */}
          <div className="form-section">
            <div className="form-section-title">
              <span className="section-icon">{getNavigationIcon('users')}</span>
              4. Target User Roles
            </div>
            <div className="checkbox-group">
              {roles.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', textAlign: 'center', margin: '1rem 0' }}>
                  Loading roles...
                </p>
              ) : (
                roles.map(role => (
                  <div
                    key={role.id}
                    className={`checkbox-item ${formData.target_roles.includes(role.id) ? 'selected' : ''}`}
                    onClick={() => handleRoleToggle(role.id)}
                  >
                    <input
                      type="checkbox"
                      checked={formData.target_roles.includes(role.id)}
                      onChange={() => {}}
                    />
                    <label>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                        {role.name}
                      </div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                        {role.description}
                      </div>
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* User Targeting (New vs Existing) */}
          <div className="form-section">
            <div className="form-section-title">
              <span className="section-icon">{getNavigationIcon('filter')}</span>
              5. User Targeting
            </div>

            <div className="form-group">
              <label className="form-label">Which users should receive this requirement?</label>
              <div className="checkbox-group">
                {[
                  { value: 'all_users', label: 'All Users', desc: 'Both existing and new users', icon: 'users' },
                  { value: 'existing_only', label: 'Existing Users Only', desc: 'Only users who existed before this requirement', icon: 'clipboard' },
                  { value: 'new_only', label: 'New Users Only', desc: 'Only users who register after this requirement', icon: 'star' },
                  { value: 'registration_triggered', label: 'Registration Triggered', desc: 'Assigned automatically on user registration', icon: 'bell' }
                ].map(targeting => (
                  <div
                    key={targeting.value}
                    className={`checkbox-item ${formData.user_targeting === targeting.value ? 'selected' : ''}`}
                    onClick={() => handleInputChange('user_targeting', targeting.value)}
                  >
                    <input
                      type="radio"
                      name="user_targeting"
                      checked={formData.user_targeting === targeting.value}
                      onChange={() => {}}
                    />
                    <label>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{targeting.label}</div>
                      <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>{targeting.desc}</div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mandatory Toggle */}
          <div className="form-section">
            <div className="form-section-title">
              <span className="section-icon">{getNavigationIcon('shield')}</span>
              6. Requirement Priority
            </div>

            <div className="mandatory-toggle-container">
              <div className="toggle-row">
                <div className={`switch ${formData.is_mandatory ? 'active' : ''}`} onClick={() => handleInputChange('is_mandatory', !formData.is_mandatory)}>
                  <div className="switch-handle"></div>
                </div>
                <div className="toggle-content" onClick={() => handleInputChange('is_mandatory', !formData.is_mandatory)}>
                  <div className="toggle-label">
                    <strong>Mandatory Requirement</strong>
                  </div>
                  <div className="toggle-description">
                    {formData.is_mandatory ? 'This requirement blocks user actions until completed' : 'This requirement is optional and appears in user inbox'}
                  </div>
                </div>
              </div>

              <div className={`status-indicator ${formData.is_mandatory ? 'mandatory' : 'optional'}`}>
                {formData.is_mandatory ? (
                  <><span className="section-icon">{getNavigationIcon('block')}</span> Process Blocking<br />Users cannot proceed without completion</>
                ) : (
                  <><span className="section-icon">{getNavigationIcon('document')}</span> Optional Requirement<br />Users can complete at their convenience</>
                )}
              </div>
            </div>

            <div className="mandatory-toggle-container" style={{ marginTop: '1.5rem' }}>
              <div className="toggle-row">
                <div className={`switch ${formData.active ? 'active' : ''}`} onClick={() => handleInputChange('active', !formData.active)}>
                  <div className="switch-handle"></div>
                </div>
                <div className="toggle-content" onClick={() => handleInputChange('active', !formData.active)}>
                  <div className="toggle-label">
                    <strong>Active Requirement</strong>
                  </div>
                  <div className="toggle-description">
                    {formData.active ? 'This requirement is currently active and will be assigned to users' : 'This requirement is inactive and will not be assigned'}
                  </div>
                </div>
              </div>

              <div className={`status-indicator ${formData.active ? 'mandatory' : 'optional'}`}>
                {formData.active ? (
                  <><span className="section-icon">{getNavigationIcon('checkmark')}</span> Active<br />Requirement is enabled</>
                ) : (
                  <><span className="section-icon">{getNavigationIcon('pause')}</span> Inactive<br />Requirement is disabled</>
                )}
              </div>
            </div>
          </div>

          {/* Conditions Section */}
          <div className="form-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div className="form-section-title">
                <span className="section-icon">{getNavigationIcon('search')}</span>
                7. Advanced Conditions (Optional)
              </div>
              <button
                type="button"
                className="button outline"
                onClick={() => setShowConditions(!showConditions)}
              >
                {showConditions ? 'Hide' : 'Show'} Conditions
              </button>
            </div>

            {showConditions && (
              <div className="conditions-section">
                {formData.conditions.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary)', textAlign: 'center', margin: '1rem 0' }}>
                    No conditions added. This requirement will apply to all selected roles.
                  </p>
                ) : (
                  <div style={{ marginBottom: '1rem' }}>
                    {formData.conditions.map((condition, index) => (
                      <div key={index} style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '6px',
                        padding: '1rem',
                        marginBottom: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                      }}>
                        <select
                          value={condition.condition_type}
                          onChange={(e) => updateCondition(index, 'condition_type', e.target.value)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '4px',
                            padding: '0.5rem',
                            color: 'rgba(255, 255, 255, 0.9)'
                          }}
                        >
                          <option value="age_comparison">Age Comparison</option>
                          <option value="program_type">Program Type</option>
                          <option value="registration_date">Registration Date</option>
                          <option value="role_assignment">Role Assignment</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Field (e.g., player_age)"
                          value={condition.field}
                          onChange={(e) => updateCondition(index, 'field', e.target.value)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '4px',
                            padding: '0.5rem',
                            color: 'rgba(255, 255, 255, 0.9)',
                            flex: 1
                          }}
                        />
                        <select
                          value={condition.operator}
                          onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '4px',
                            padding: '0.5rem',
                            color: 'rgba(255, 255, 255, 0.9)'
                          }}
                        >
                          <option value="==">=</option>
                          <option value="!=">≠</option>
                          <option value="<">&lt;</option>
                          <option value=">">&gt;</option>
                          <option value="<=">&le;</option>
                          <option value=">=">&ge;</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Value"
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', e.target.value)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '4px',
                            padding: '0.5rem',
                            color: 'rgba(255, 255, 255, 0.9)',
                            width: '100px'
                          }}
                        />
                        <button
                          type="button"
                          className="condition-remove"
                          onClick={() => removeCondition(index)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button type="button" className="button outline" onClick={addCondition}>
                  + Add Condition
                </button>
              </div>
            )}
          </div>

          {/* Notification Settings */}
          <div className="form-section">
            <div className="form-section-title">
              <span className="section-icon">{getNavigationIcon('bell')}</span>
              8. Notification Settings
            </div>
            <div className="checkbox-group">
              <div className={`checkbox-item ${formData.send_email_notifications ? 'selected' : ''}`}
                   onClick={() => handleInputChange('send_email_notifications', !formData.send_email_notifications)}>
                <input
                  type="checkbox"
                  checked={formData.send_email_notifications}
                  onChange={() => {}}
                />
                <label>
                  <span className="section-icon">{getNavigationIcon('mail')}</span>
                  Email Notifications
                </label>
              </div>
              <div className={`checkbox-item ${formData.send_sms_notifications ? 'selected' : ''}`}
                   onClick={() => handleInputChange('send_sms_notifications', !formData.send_sms_notifications)}>
                <input
                  type="checkbox"
                  checked={formData.send_sms_notifications}
                  onChange={() => {}}
                />
                <label>
                  <span className="section-icon">{getNavigationIcon('bell')}</span>
                  SMS Notifications
                </label>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="button-group">
            <button type="button" className="button secondary" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="submit"
              className="button primary"
              disabled={loading || !formData.template_id || !formData.title}
            >
              {loading ? 'Creating...' : (
                <><span className="section-icon">{getNavigationIcon('checkmark')}</span> Create Requirement</>
              )}
            </button>
          </div>
        </form>
    </div>
  );
};

export default DocumentRequirementConfigurator;